import React from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { COMPANY_DETAILS_SECTIONS } from "../config/companyDetailsForm";
import { SettingsPanel } from "../components/SettingsPanel";
import { SettingsSection } from "../components/SettingsSection";
import { SettingsLayout } from "../layout/SettingsLayout";
import { useCompanySettings } from "../hooks/useCompanySettings";

const PanelSkeleton: React.FC = () => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
      <span className="text-body font-body text-subtext-color">
        Loading company details...
      </span>
    </div>
  </div>
);

const PanelError: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex max-w-md flex-col items-center gap-4 text-center">
      <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
      <span className="text-heading-3 font-heading-3 text-default-font">
        We couldn't load your company details
      </span>
      <span className="text-body font-body text-subtext-color">{message}</span>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  </div>
);

/**
 * CompanyDetailsPage — `/settings/company-details`.
 *
 * The tenant's own copy of the company basics the master onboarding wizard
 * collects. After go-live the tenant owns this record, so this screen is where
 * it changes; the legal identity fields stay read-only because they drive
 * filings and every document pushed to the books system.
 *
 * The form is generated from `COMPANY_DETAILS_SECTIONS` plus the API payload,
 * so this component stays a state-to-layout wiring and nothing else.
 */
export const CompanyDetailsPage: React.FC = () => {
  const {
    settings,
    values,
    loading,
    saving,
    error,
    fieldErrors,
    dirty,
    saved,
    setField,
    save,
    discard,
    reload,
  } = useCompanySettings();

  if (loading) {
    return (
      <SettingsLayout>
        <PanelSkeleton />
      </SettingsLayout>
    );
  }

  if (!settings) {
    return (
      <SettingsLayout>
        <PanelError message={error ?? "Something went wrong."} onRetry={reload} />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <SettingsPanel
        title="Company details"
        description="Your registered company information and what the business does."
        error={error}
        saved={saved}
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={discard}
      >
        {COMPANY_DETAILS_SECTIONS.map((section) => (
          <SettingsSection
            key={section.id}
            config={section}
            settings={settings}
            values={values}
            fieldErrors={fieldErrors}
            onChange={setField}
          />
        ))}
      </SettingsPanel>
    </SettingsLayout>
  );
};
